import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useFormTaskSseBus from './useFormTaskSseBus';
import { pushGlobalToast } from '../components/v2/GlobalToastStack';

/**
 * useFormTaskToastNotifier — RC-224 global toast wire-up.
 *
 * <p>Subscribes to the form-task SSE bus and fires a global toast on
 * {@code form_task_assigned} events so a user sees the notification
 * from any page in the app, not just /forms/to-do.</p>
 *
 * <p>Mounted ONCE from the app shell (DashboardLayoutV2). Calling it
 * twice would double-fire toasts — guarded with a module-level flag
 * so misuse fails loudly in dev.</p>
 *
 * <h3>Suppression rules</h3>
 * <ul>
 *   <li><b>On the /forms/to-do page itself</b> — suppress the toast.
 *       The page reloads the list on the same event (via the same SSE
 *       bus), so the user already sees the new task immediately;
 *       firing a toast on top would be redundant noise.</li>
 *   <li><b>On the /forms/to-do/{taskId} fill page</b> — suppress for
 *       the same reason: the user is already engaged with the form
 *       surface.</li>
 *   <li><b>Self-suppression after click</b> — clicking the toast
 *       navigates to /forms/to-do/{taskId}; the toast dismisses itself
 *       via the stack's auto-dismiss.</li>
 * </ul>
 *
 * <h3>Payload contract</h3>
 * The BE emits this shape from {@code MyFormTasksEventPublisher.publish}:
 * <pre>
 *   {
 *     "taskId": "&lt;uuid string&gt;",
 *     "formLabel": "Triage incident 4521",
 *     "at": "2026-06-23T14:32:01Z"
 *   }
 * </pre>
 * Missing fields are tolerated; the toast falls back to "a form task"
 * if the label is absent.
 */
export default function useFormTaskToastNotifier() {
  const navigate = useNavigate();
  const location = useLocation();

  // Keep latest path in a ref so the SSE handler (stable identity per
  // useFormTaskSseBus contract) reads the current path without forcing
  // re-subscription on every navigation.
  const locationRef = useRef(location.pathname);
  useEffect(() => { locationRef.current = location.pathname; }, [location.pathname]);

  const handle = useCallback((eventName, payload) => {
    // Suppress on Forms pages — see header comment.
    const path = locationRef.current || '';
    if (path.startsWith('/forms/to-do') || path.startsWith('/my-tasks')) {
      return;
    }
    const taskId = payload?.taskId;
    const label = payload?.formLabel || 'A form task';
    pushGlobalToast({
      tone: 'info',
      title: 'New form task assigned',
      description: label,
      // Auto-dismiss after 7s — long enough to read, short enough not to
      // accumulate on a busy stream. The stack's MAX_VISIBLE cap is the
      // safety net.
      duration: 7000,
      // Clickable action that navigates to the fill page if we know the id.
      action: taskId ? (
        <button
          type="button"
          onClick={() => navigate(`/forms/to-do/${taskId}`)}
          className={
            // V2 token utilities — no inline style, no hex. Matches the
            // link styling used elsewhere in toasts (link-action).
            'v2-focus-ring text-[12px] font-semibold text-v2-action ' +
            'hover:text-v2-action-strong transition-colors duration-v2 ease-v2'
          }
        >
          Open form →
        </button>
      ) : null,
    });
  }, [navigate]);

  useFormTaskSseBus('form_task_assigned', handle);

  // Dev-mode misuse guard. Double-mounting this hook (e.g. accidentally
  // mounting two DashboardLayoutV2 instances during a layout refactor)
  // would double-fire every toast. We can't enforce singleton-ness from
  // inside React without contortions, but we can flag it loudly in
  // development.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined;
    mountCount += 1;
    if (mountCount > 1) {
      // eslint-disable-next-line no-console
      console.warn(
        '[useFormTaskToastNotifier] mounted %d times — toasts will fire %d× per event. ' +
        'Mount it exactly ONCE in the app shell.',
        mountCount, mountCount,
      );
    }
    return () => { mountCount -= 1; };
  }, []);
}

let mountCount = 0;
